import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Mail, Lock, Loader2, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useProducerAuth } from '../../context/ProducerAuthContext';
import { Button } from '../../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_rebill-payment-fix/artifacts/vodl6hhv_Blanco-Icono.png";

const ProducerLoginPage = () => {
  const { login } = useProducerAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/producers/login', formData);
      const userData = response.data.producer;
      
      // Store token + producer in the auth context (loads permissions without a reload)
      login(response.data.access_token, userData);
      
      // Check if user is a validator
      if (userData.is_validator) {
        toast.success(`¡Bienvenido, ${userData.name}!`);
        // Redirect validators to scanner page
        navigate('/producer/scanner');
      } else {
        toast.success(`¡Bienvenido, ${userData.business_name || userData.name}!`);
        navigate('/producer/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      {/* Background Effect */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-violet-500/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-violet-500/5 rounded-full blur-[100px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <Link to="/" className="flex justify-center mb-8">
          <img src={LOGO_URL} alt="Bravo" className="h-16" />
        </Link>

        {/* Card */}
        <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-violet-500/10 border border-violet-500/30 rounded-full px-4 py-2 mb-4">
              <Building2 className="text-violet-500" size={16} />
              <span className="text-violet-500 text-sm font-medium">Portal Productoras</span>
            </div>
            <h1 className="text-2xl font-black text-white">Iniciar Sesión</h1>
            <p className="text-zinc-400 text-sm mt-2">Accedé al panel de tu productora</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-zinc-500 focus:border-violet-500 focus:outline-none"
                  placeholder="email@productora.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-zinc-400 mb-2">Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-12 py-3 text-white placeholder-zinc-500 focus:border-violet-500 focus:outline-none"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-violet-600 to-pink-600 text-white font-bold"
            >
              {loading ? (
                <Loader2 className="animate-spin mx-auto" />
              ) : (
                <>
                  Ingresar
                  <ArrowRight size={18} className="ml-2" />
                </>
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-zinc-800 text-center">
            <p className="text-zinc-500 text-sm">
              ¿No tenés cuenta?{' '}
              <Link to="/productoras/aplicar" className="text-violet-500 hover:underline">
                Registrá tu productora
              </Link>
            </p>
          </div>
        </div>

        {/* Back to home */}
        <div className="text-center mt-6">
          <Link to="/" className="text-zinc-500 hover:text-zinc-300 text-sm">
            ← Volver al inicio
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default ProducerLoginPage;
